import { useEffect, useRef, useState } from "react";

// Types of indexes in the slider. This enum determines the visual state of an "index circle"
export enum SliderIndex {
    Active = "A",
    Large = "L",
    Small = "S",
    Mini = "M", // Mini is an image but hidden as a button
    Empty = "", // Empty is not an image but a placeholder, used for positioning purposes.
}

// Mapping for the pixel size of an index circle.
export const sizeMap = {
    [SliderIndex.Active]: 6,
    [SliderIndex.Large]: 6,
    [SliderIndex.Small]: 4,
    [SliderIndex.Mini]: 0,
    [SliderIndex.Empty]: 0,
};

/**
 * Generate an initial index array to be used in the slider.
 * We're assuming the first render of this component is always with the first image selected.
 */
const generateInitialIndex = (itemCount: number): SliderIndex[] => {
    const sliderArray: SliderIndex[] = [];

    if (itemCount > 5) {
        // 5+ images so we need to animate the slider. Create the array needed for this.
        for (let i = 0; i < itemCount; i++) {
            if (i === 0) sliderArray.push(SliderIndex.Empty, SliderIndex.Empty, SliderIndex.Active);
            else if (i < 3) sliderArray.push(SliderIndex.Large);
            else if (i === 3) sliderArray.push(SliderIndex.Small);
            else sliderArray.push(SliderIndex.Mini);
        }
    } else {
        // 5 or less images so we don't need the animation which requires mini/small indexes.
        // In this case we just need to "fill" the array with the amount of circles required and add empty padding to make sure the positioning is consistent.

        // Add active/inactive indexes for every item.
        for (let i = 0; i < itemCount; i++) {
            if (i === 0) sliderArray.push(SliderIndex.Active);
            else sliderArray.push(SliderIndex.Large);
        }

        // Add padding so we always get an array with 7 values which is required for optimal positioning.
        const fill = 7 - sliderArray.length;
        for (let i = 0; i < 7 - fill; i++) {
            sliderArray.unshift(SliderIndex.Empty);
        }
    }
    return sliderArray;
};

type UseImageSliderIndexType = { sliderArray: SliderIndex[]; leftOffset: number };

const useImageSliderIndex = (itemCount: number, activeItemIndex: number): UseImageSliderIndexType => {
    const [sliderArray, setSliderArray] = useState<SliderIndex[]>(generateInitialIndex(itemCount));
    const [leftOffset, setLeftOffset] = useState<number>(0);
    const previousActiveIndex = useRef<number>(activeItemIndex);
    const previousItemCount = useRef<number>(itemCount);

    // Animate transition logic.
    useEffect(() => {
        // Only trigger when the index or the item count changes.
        if (previousActiveIndex.current === activeItemIndex && previousItemCount.current === itemCount) return;

        // Edge case: reset of the slider
        if (activeItemIndex === 0 && previousActiveIndex.current !== 1) {
            setSliderArray(generateInitialIndex(itemCount));
            setLeftOffset(0);
            return;
        }

        // Determine if we're sliding left or right as that impacts how we'll interpret the array.
        const isLeftSlide = previousActiveIndex.current > activeItemIndex;

        // Create a new array so we can set this one in state and avoid reference bugs.
        let newSliderArray = [...sliderArray];

        // <-- If we're sliding to the left, reverse the array: <--
        if (isLeftSlide) newSliderArray = newSliderArray.reverse();
        // This is a trick to prevent having to copy paste a lot of similar code for left and right slides
        // as it should basically have the same behaviour but reversed.
        // By reversing on a left slide we can do all of our calculations based on a right slide,
        // and re-reverse the array at the end to have this logic magically working as a left slide.
        // ⚠️ .reverse() doesn't return a new array, so be careful with references.

        // Get the index of the current active item.
        const currentIndex = newSliderArray.findIndex((sliderIndex) => sliderIndex === SliderIndex.Active);

        // Determine which changes to the layout need to happen based on the type of the next item we're sliding to.
        switch (newSliderArray[currentIndex + 1]) {
            case SliderIndex.Large:
                // The next slider item is a large circle, set active and make sure the current index is no longer active.
                newSliderArray[currentIndex + 1] = SliderIndex.Active;
                newSliderArray[currentIndex] = SliderIndex.Large;
                break;
            case SliderIndex.Small:
                // The next slider item is a small circle.
                // Set active, make sure the current index is no longer active, and make sure that there are only 3 large items available.
                if (newSliderArray[currentIndex + 2]) newSliderArray[currentIndex + 2] = SliderIndex.Small;
                newSliderArray[currentIndex + 1] = SliderIndex.Active;
                newSliderArray[currentIndex] = SliderIndex.Large;
                if (newSliderArray[currentIndex - 1]) newSliderArray[currentIndex - 1] = SliderIndex.Large;
                if (newSliderArray[currentIndex - 2]) newSliderArray[currentIndex - 2] = SliderIndex.Small;
                if (newSliderArray[currentIndex - 3]) newSliderArray[currentIndex - 3] = SliderIndex.Mini;
                break;
        }

        // Set the new slider array, re-reverse the array if we are sliding left.
        setSliderArray(isLeftSlide ? newSliderArray.reverse() : newSliderArray);

        // Determine left offset. This value is used to keep our "large" circles at the same place at all times.
        // This only needs to happen if we have 5+ items.
        if (itemCount > 5) {
            const firstActiveIndex = newSliderArray.findIndex((sliderIndex) =>
                [SliderIndex.Large, SliderIndex.Active].includes(sliderIndex),
            );
            if (firstActiveIndex > 2) setLeftOffset(firstActiveIndex - 2);
            else setLeftOffset(0);
        }

        previousActiveIndex.current = activeItemIndex;
        previousItemCount.current = itemCount;
    }, [activeItemIndex, itemCount]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        sliderArray,
        leftOffset,
    };
};

export default useImageSliderIndex;
